import { useEffect, useCallback, useRef, useState } from "react";

export type GestureDirection = "left" | "right" | "up" | "down";
export type GestureAction = "swipe" | "longpress" | "doubletap";

interface UseAdvancedGesturesProps {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onLongPress?: () => void;
  onDoubleTap?: () => void;
  swipeThreshold?: number;
  longPressDelay?: number;
  doubleTapDelay?: number;
  hapticFeedback?: boolean;
  enabled?: boolean;
}

interface GestureFeedback {
  direction: GestureDirection | null;
  action: GestureAction | null;
}

export const useAdvancedGestures = ({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  onLongPress,
  onDoubleTap,
  swipeThreshold = 50,
  longPressDelay = 500,
  doubleTapDelay = 300,
  hapticFeedback = true,
  enabled = true
}: UseAdvancedGesturesProps) => {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const lastTapTime = useRef<number>(0);
  const [feedback, setFeedback] = useState<GestureFeedback>({ direction: null, action: null });

  const triggerHaptic = useCallback((intensity: "light" | "medium" | "heavy" = "light") => {
    if (!hapticFeedback || !enabled) return;
    
    // iOS Taptic Engine
    if ('vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [20],
        heavy: [30]
      };
      navigator.vibrate(patterns[intensity]);
    }
  }, [hapticFeedback, enabled]);

  const showFeedback = useCallback((direction: GestureDirection | null, action: GestureAction) => {
    setFeedback({ direction, action });
    setTimeout(() => setFeedback({ direction: null, action: null }), 300);
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return;
    
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchStartTime.current = Date.now();
    
    // Long press detection
    longPressTimer.current = setTimeout(() => {
      if (onLongPress) {
        onLongPress();
        triggerHaptic("medium");
        showFeedback(null, "longpress");
      }
    }, longPressDelay);
    
    // Double tap detection
    const now = Date.now();
    const timeSinceLastTap = now - lastTapTime.current;
    if (timeSinceLastTap < doubleTapDelay && timeSinceLastTap > 0) {
      if (onDoubleTap) {
        onDoubleTap();
        triggerHaptic("light");
        showFeedback(null, "doubletap");
      }
      lastTapTime.current = 0;
    } else {
      lastTapTime.current = now;
    }
  }, [enabled, onLongPress, onDoubleTap, longPressDelay, doubleTapDelay, triggerHaptic, showFeedback]);

  const handleTouchMove = useCallback(() => {
    // Cancel long press if user moves finger
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || touchStartX.current === null || touchStartY.current === null) return;

    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;
    
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // Determine if it's a swipe and in which direction
    if (absDeltaX > absDeltaY && absDeltaX > swipeThreshold) {
      // Horizontal swipe
      if (deltaX > 0) {
        onSwipeRight?.();
        triggerHaptic("light");
        showFeedback("right", "swipe");
      } else {
        onSwipeLeft?.();
        triggerHaptic("light");
        showFeedback("left", "swipe");
      }
    } else if (absDeltaY > absDeltaX && absDeltaY > swipeThreshold) {
      // Vertical swipe
      if (deltaY > 0) {
        onSwipeDown?.();
        triggerHaptic("light");
        showFeedback("down", "swipe");
      } else {
        onSwipeUp?.();
        triggerHaptic("light");
        showFeedback("up", "swipe");
      }
    }
    
    touchStartX.current = null;
    touchStartY.current = null;
    touchStartTime.current = null;
  }, [enabled, swipeThreshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, triggerHaptic, showFeedback]);

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    feedback,
    triggerHaptic
  };
};
